import { PageShell } from "@superadmin/components/PageShell";
import {
	AddModelForm,
	AddProviderForm,
	EditModelForm,
	EditProviderForm,
	type ModelDraft,
	type ModelPatch,
	type ProviderDraft,
	type ProviderPatch,
	type WriteError,
} from "@superadmin/components/ProviderForms";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import {
	byLeg,
	describeRetirement,
	isOffered,
	LEGS,
	offerableAcrossLegs,
	offeredOnAnyLeg,
	type PanelModel,
	type PanelProvider,
	retirementState,
	servedLanguages,
} from "@superadmin/utils/providers_panel";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Button } from "@voicely/ui-mui/form/Button";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Card, CardContent } from "@voicely/ui-mui/layout/Card";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import { describeLanguages } from "@voicely/zod-schemas/languages";
import type { ProviderCapabilityOffer } from "@voicely/zod-schemas/provider.zod";
import { toast } from "react-toastify";
import { z } from "zod";

/**
 * The vendor catalogue, as a super-admin edits it.
 *
 * ## What this page is for
 *
 * Adding and retiring a model used to be a deploy. Vendors withdraw models on
 * their own schedule and do not ask — the Sonex roster went 26 names to 44 to
 * 38 — so "stop offering Bulbul v2" waited on a release. It is rows now, and
 * this is where they are written.
 *
 * It was read-only for its first life, and in the tenant bundle rather than
 * this one: a super-admin-gated route at `/providers` in the customer app,
 * linked from nothing, calling `listAll` and `usage` and nothing else. The six
 * write procedures had no caller anywhere. Moving it here put it beside the
 * other eight operator screens, where somebody looking for it would look.
 *
 * ## What it deliberately does not let you do
 *
 * Claim a capability. A row says which models are OFFERED; what an adapter can
 * technically DO — which model ids it knows, which languages it serves from
 * India, which encodings, whether the vendor can cancel a generation — is
 * declared in the realtime runtime's code. The forms are built FROM that
 * declaration, so the usual way of getting it wrong is not reachable, and the
 * save is still checked against the same snapshot: a redeploy between the page
 * load and the click can move the ceiling under a menu drawn from the old
 * answer.
 *
 * A save can also be refused because the runtime is unreachable. That is
 * deliberate rather than a rough edge — a row written without the check is
 * exactly the unchecked row the check exists to prevent — and the refusal is
 * shown verbatim, because it is written to be read.
 *
 * ## Why nothing here publishes on create
 *
 * Both create forms write a draft. A vendor or a model that goes live in the
 * same click that creates it has never been looked at by anybody, including the
 * person who made it, and this catalogue decides what every workspace on the
 * platform can build an agent out of.
 *
 * ## Why the open form is in the URL
 *
 * House rule: anything the user can see lives in the route. A retirement is the
 * kind of decision somebody opens in a second tab to check against an agent
 * list, and a dialog held in component memory cannot be linked to or survive
 * the refresh they will inevitably do. What is NOT in the route is what they
 * have half-typed into it — a URL lives in history, in logs, and in whatever
 * gets pasted into a chat window.
 */

/**
 * Which form is open, and what it is about.
 *
 * One parameter rather than four booleans, because exactly one of these is ever
 * open and four independent flags can express states that do not exist. A value
 * naming a row that has since been deleted degrades to "closed" rather than
 * throwing, which is what `useUrlState`'s validation is for.
 */
const panelZod = z
	.string()
	.regex(/^(new-provider|provider:[\w-]+|new-model:[\w-]+|model:[\w-]+)$/)
	.nullable();

export const ProvidersPage = () => {
	const [panel, setPanel] = useUrlState<string | null>("panel", panelZod, null);
	const close = () => setPanel(null);

	const queryClient = useQueryClient();

	const catalogueQuery = useQuery(orpc.providers.listAll.queryOptions());

	// Fetched with the catalogue rather than when a form opens: every form on
	// this page is built out of it, and a dropdown that appears a beat after the
	// dialog reads as a broken dialog.
	const capabilitiesQuery = useQuery(orpc.providers.capabilities.queryOptions());

	// Recomputed per render rather than pinned at mount: a tab left open across
	// a retirement date should start telling the truth without a reload.
	const now = Date.now();
	const providers = (catalogueQuery.data ?? []) as PanelProvider[];
	const capabilities = (capabilitiesQuery.data ?? []) as ProviderCapabilityOffer[];

	/**
	 * Every write refetches the catalogue and says so.
	 *
	 * `listAll` is one query and the page is one screen, so invalidating the lot
	 * is cheaper than reasoning about which card moved — and a panel that
	 * disagrees with the database it just wrote to is the specific confusion
	 * this page cannot afford.
	 */
	const afterWrite = (message: string) => ({
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: orpc.providers.key() });
			toast.success(message);
			setPanel(null);
		},
	});

	const createProvider = useMutation(
		orpc.providers.createProvider.mutationOptions(afterWrite("Vendor created as a draft.")),
	);
	const updateProvider = useMutation(
		orpc.providers.updateProvider.mutationOptions(afterWrite("Vendor saved.")),
	);
	const deleteProvider = useMutation(
		orpc.providers.deleteProvider.mutationOptions(afterWrite("Vendor and its models deleted.")),
	);
	const createModel = useMutation(
		orpc.providers.createModel.mutationOptions(afterWrite("Model created as a draft.")),
	);
	const updateModel = useMutation(
		orpc.providers.updateModel.mutationOptions(afterWrite("Model saved.")),
	);
	const deleteModel = useMutation(
		orpc.providers.deleteModel.mutationOptions(afterWrite("Model deleted.")),
	);

	const saving = [
		createProvider,
		updateProvider,
		deleteProvider,
		createModel,
		updateModel,
		deleteModel,
	].some((mutation) => mutation.isPending);

	/** The server's refusal, as the form should render it. */
	const errorOf = (mutation: { error: unknown }): WriteError | null => {
		if (!mutation.error) return null;

		const error = mutation.error as { message?: string; data?: { field?: string } };

		return { message: error.message ?? "The save was refused.", field: error.data?.field };
	};

	const flat = providers.flatMap((provider) =>
		provider.models.map((model) => ({ provider, model })),
	);

	const editingProvider = panel?.startsWith("provider:")
		? providers.find((entry) => entry.id === panel.slice("provider:".length))
		: undefined;

	const addingModelTo = panel?.startsWith("new-model:")
		? providers.find((entry) => entry.id === panel.slice("new-model:".length))
		: undefined;

	const editingModel = panel?.startsWith("model:")
		? flat.find((entry) => entry.model.id === panel.slice("model:".length))
		: undefined;

	// Two counting queries, so they are asked only for the model actually open.
	const usageQuery = useQuery({
		...orpc.providers.usage.queryOptions({
			input: {
				kind: editingModel?.provider.kind ?? "stt",
				modelId: editingModel?.model.modelId ?? "",
			},
		}),
		enabled: Boolean(editingModel),
	});

	if (catalogueQuery.isPending) {
		return (
			<PageShell title="Providers">
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (catalogueQuery.error) {
		return (
			<PageShell title="Providers">
				<Alert severity="error">
					Could not load the catalogue. Nothing has changed — {catalogueQuery.error.message}
				</Alert>
			</PageShell>
		);
	}

	const offerable = offerableAcrossLegs(providers, now);
	const onMenu = offeredOnAnyLeg(providers, now);

	return (
		<PageShell
			title="Providers"
			subtitle="Which vendors and models a workspace may choose. What each one can technically do is declared by its adapter in the runtime and checked on every save — a row here decides what is offered, never what is possible."
			actions={
				<Button
					variant="contained"
					onClick={() => setPanel("new-provider")}
					disabled={capabilitiesQuery.isPending || Boolean(capabilitiesQuery.error)}
				>
					Add a vendor
				</Button>
			}
		>
			<Stack spacing={3}>
				{/* The forms are built from the runtime's declaration, so an
				    unreachable runtime is not a degraded read here — it is the
				    difference between a menu of what exists and a blank one. The
				    writes would refuse for the same reason and say so. */}
				{capabilitiesQuery.error && (
					<Alert severity="warning">
						Could not reach the realtime runtime, so this page can show the rows but cannot offer
						the vendors and models to add to them — and a save would be refused for the same reason.{" "}
						{capabilitiesQuery.error.message}
					</Alert>
				)}

				<Alert severity="info">
					<Typography variant="body2">
						<strong>Agents can be built in: {describeLanguages(offerable)}.</strong> Workspaces can
						pick from {onMenu.length} languages — every one a published model serves from India. A
						language on the menu but not in that list is missing a leg, and the leg with the
						shortest list below is what is holding it back.
					</Typography>
				</Alert>

				{LEGS.map((leg) => {
					const entries = byLeg(providers, leg.kind);
					const served = servedLanguages(providers, leg.kind, now);

					return (
						<Box key={leg.kind}>
							<Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
								<Typography variant="h6">{leg.label}</Typography>
								<Typography variant="caption" color="text.secondary">
									serves {describeLanguages(served)}
								</Typography>
							</Stack>

							<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
								{leg.blurb}
							</Typography>

							<Stack spacing={1.5}>
								{entries.map((provider) => (
									<ProviderCard
										key={provider.id}
										provider={provider}
										now={now}
										onEdit={() => setPanel(`provider:${provider.id}`)}
										onAddModel={() => setPanel(`new-model:${provider.id}`)}
										onEditModel={(model) => setPanel(`model:${model.id}`)}
										canAddModel={!capabilitiesQuery.isPending && !capabilitiesQuery.error}
									/>
								))}

								{entries.length === 0 && (
									<Alert severity="warning">
										No vendor is offered for this leg, so no agent can be created. A vendor has to
										exist in the runtime before it can be added here.
									</Alert>
								)}
							</Stack>
						</Box>
					);
				})}
			</Stack>

			{panel === "new-provider" && (
				<AddProviderForm
					open
					capabilities={capabilities}
					providers={providers}
					saving={saving}
					error={errorOf(createProvider)}
					onSave={(draft: ProviderDraft) => createProvider.mutate(draft)}
					onClose={close}
				/>
			)}

			{editingProvider && (
				<EditProviderForm
					// Remounts per row, so the form's initial values come from the row
					// it is about rather than from whichever was opened first.
					key={editingProvider.id}
					open
					provider={editingProvider}
					saving={saving}
					error={errorOf(updateProvider) ?? errorOf(deleteProvider)}
					onSave={(patch: ProviderPatch) =>
						updateProvider.mutate({ id: editingProvider.id, ...patch })
					}
					onDelete={() => deleteProvider.mutate({ id: editingProvider.id })}
					onClose={close}
				/>
			)}

			{addingModelTo && (
				<AddModelForm
					key={addingModelTo.id}
					open
					provider={addingModelTo}
					capabilities={capabilities}
					saving={saving}
					error={errorOf(createModel)}
					onSave={(draft: ModelDraft) =>
						createModel.mutate({ providerId: addingModelTo.id, ...draft })
					}
					onClose={close}
				/>
			)}

			{editingModel && (
				<EditModelForm
					key={editingModel.model.id}
					open
					provider={editingModel.provider}
					model={editingModel.model}
					capabilities={capabilities}
					saving={saving}
					error={errorOf(updateModel) ?? errorOf(deleteModel)}
					usage={usageQuery.data ?? null}
					onSave={(patch: ModelPatch) =>
						updateModel.mutate({ id: editingModel.model.id, ...patch })
					}
					onDelete={() => deleteModel.mutate({ id: editingModel.model.id })}
					onClose={close}
				/>
			)}
		</PageShell>
	);
};

function ProviderCard({
	provider,
	now,
	onEdit,
	onAddModel,
	onEditModel,
	canAddModel,
}: {
	provider: PanelProvider;
	now: number;
	onEdit: () => void;
	onAddModel: () => void;
	onEditModel: (model: PanelModel) => void;
	canAddModel: boolean;
}) {
	return (
		<Card variant="outlined">
			<CardContent>
				<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
					<Typography variant="subtitle1">{provider.label}</Typography>
					<Chip size="small" label={provider.name} variant="outlined" />
					<Chip
						size="small"
						// Null residency is not missing data — it means the country
						// belongs to the MODEL, which is true of any gateway fronting
						// separately-deployed models.
						label={provider.residency ?? "country depends on the model"}
						color={provider.residency === "India" ? "success" : "default"}
						variant="outlined"
					/>
					{!provider.published && <Chip size="small" label="Draft" color="warning" />}
					<Box sx={{ flexGrow: 1 }} />
					<Button size="small" onClick={onEdit}>
						Edit
					</Button>
					<Button size="small" onClick={onAddModel} disabled={!canAddModel}>
						Add model
					</Button>
				</Stack>

				{provider.description && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						{provider.description}
					</Typography>
				)}

				{provider.models.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No models yet, so nobody can select this vendor. Every provider needs at least one row —
						it is what carries the speed score, the retirement date and the successor. A vendor
						whose wire takes no model parameter still gets one; its adapter drops the id.
					</Typography>
				) : (
					<Stack spacing={0.5}>
						{provider.models.map((model) => (
							<ModelRow
								key={model.id}
								model={model}
								now={now}
								onSelect={() => onEditModel(model)}
							/>
						))}
					</Stack>
				)}
			</CardContent>
		</Card>
	);
}

function ModelRow({
	model,
	now,
	onSelect,
}: {
	model: PanelModel;
	now: number;
	onSelect: () => void;
}) {
	const state = retirementState(model, now);
	const offered = isOffered(model, now);
	const retirement = describeRetirement(model, now, (epoch) =>
		new Date(epoch).toLocaleDateString(),
	);

	return (
		<Box
			component="button"
			type="button"
			onClick={onSelect}
			sx={{
				textAlign: "left",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1,
				p: 1,
				background: "none",
				cursor: "pointer",
				width: "100%",
			}}
		>
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
				<Typography variant="body2">{model.label}</Typography>
				<Typography variant="caption" color="text.secondary">
					{model.modelId}
				</Typography>
				{model.vendor && (
					<Chip
						size="small"
						variant="outlined"
						label={model.vendorModel ? `${model.vendor} · ${model.vendorModel}` : model.vendor}
					/>
				)}
				{model.isDefault && <Chip size="small" label="Default" color="primary" />}
				{!model.published && <Chip size="small" label="Draft" color="warning" />}
				{state === "retiring" && <Chip size="small" label="Retiring" color="warning" />}
				{state === "retired" && <Chip size="small" label="Retired" color="error" />}
				{!offered && model.published && state === "active" && (
					<Chip size="small" label="Not offered" />
				)}
			</Stack>

			<Typography variant="caption" color="text.secondary" display="block">
				{model.languages.length > 0
					? `Serves ${describeLanguages(model.languages)} from India`
					: "Serves nothing from inside India, so no agent can use it"}
				{/* Stated separately from "serves" because they are different
				    facts: nova-2 serves English and Hindi individually and cannot
				    carry both on one in-country call. This line is why a model
				    does or does not appear for bilingual agents. */}
				{(model.codeSwitching?.length ?? 0) > 0
					? ` · carries ${describeLanguages(model.codeSwitching ?? [])} on one call`
					: model.languages.length > 1
						? " · one language per call"
						: ""}
			</Typography>

			{retirement && (
				<Typography variant="caption" color="warning.main" display="block">
					{retirement}
				</Typography>
			)}
		</Box>
	);
}
